// i18n
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';

import { SyntheticEvent, useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { UserProfileItem, ViewerItem } from 'presentations/api/schema/user';
import {
  AdminSubscriptionItem,
  PreviewInvoiceBody,
  PreviewInvoiceItem,
  UpgradePlanBody,
} from 'presentations/api/schema/subscription';

import {
  adminSubscriptionPlanUpdatedPath,
  apiPath,
  dashboardPath,
} from 'presentations/routes/path';
import { get, patch, post } from 'infras/rest_client';
import { formatToSeconds, getDateTime } from 'infras/datetime';
import { IntervalType, PlanId } from 'domains/models/subscription/planId';
import { useRequest } from 'presentations/views/hooks/request';
import { viewerItem } from 'presentations/api/schema/user/mock';
import { setEvent } from 'presentations/views/helpers/analytics';

interface Props {
  viewer: ViewerItem;
  planId: PlanId | null;
  intervalType: IntervalType;
}

export function usePlanSwitchForm(props: Props) {
  const t = translateInComponent(translations);

  const router = useRouter();

  const [isInitLoading, setIsInitLoading] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [prorationTime, setProrationTime] = useState<number | null>(null);
  const [
    adminSubscription,
    setAdminSubscription,
  ] = useState<AdminSubscriptionItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [
    previewInvoiceItem,
    setPreviewInvoiceItem,
  ] = useState<PreviewInvoiceItem | null>(null);

  useEffect(() => {
    (async () => {
      setIsInitLoading(true);
      await fetch();
      setIsInitLoading(false);
    })();
  }, [props.planId]);

  const userProfileRequest = useRequest(apiPath('/v1/user/profile'));
  const userProfile: UserProfileItem = userProfileRequest.data;

  const upcomingInvoiceRequest = useRequest(
    props.viewer.team
      ? apiPath(`/v1/admin/teams/${props.viewer.team.uid}/invoices/upcoming`)
      : null,
  );

  const fetch = async () => {
    if (props.planId) {
      const result = await get(
        apiPath(`/v1/admin/teams/${props.viewer.team!.uid}/subscription`),
      );
      if (!result.isSuccess) {
        return;
      }

      const quantity = result.data.quantity! || 0;
      const prorationTime = getDateTime()
        .plus({ minutes: 1 })
        .startOf('minute');
      const data: PreviewInvoiceBody = {
        planId: props.planId,
        quantity,
        interval: props.intervalType,
        prorationTime: Math.floor(formatToSeconds(prorationTime)),
      };

      const previewResult = await post(
        apiPath(`/v1/admin/teams/${props.viewer.team!.uid}/invoices/preview`),
        { data },
      );
      if (!previewResult.isSuccess) {
        console.log(t('error api failed'));
        return;
      }
      setProrationTime(data.prorationTime);
      setAdminSubscription(result.data);
      const previewInvoice: PreviewInvoiceItem = previewResult.data;
      setPreviewInvoiceItem(previewInvoice);
    }
  };

  const onSubmit = async (e: SyntheticEvent<HTMLButtonElement>) => {
    if (!adminSubscription || !props.planId) return;
    setIsLoading(true);
    const body: UpgradePlanBody = {
      planId: props.planId,
      interval: props.intervalType,
      prorationTime: prorationTime!,
    };
    const result = await patch(
      apiPath(
        `/v1/admin/teams/${props.viewer.team!.uid}/subscription/upgrade_plan`,
      ),
      {
        data: body,
      },
    );
    if (!result.isSuccess) {
      setIsLoading(false);
      return;
    }
    upcomingInvoiceRequest.revalidate();

    router.replace(
      adminSubscriptionPlanUpdatedPath(
        {
          planId: props.planId!,
          quantity:
            adminSubscription.purchaseQuantity || adminSubscription.quantity,
          intervalType: props.intervalType,
        },
        false,
      ),
    );
    setIsLoading(false);
    setEvent('plan upgrade', {
      planId: props.planId,
      fromPlanId: props.viewer.planId,
    });
  };

  return {
    state: {
      isLoading,
      isInitLoading,
      previewInvoiceItem,
      adminSubscription,
      userProfile,
      error,
    },
    actions: {
      onSubmit,
    },
  };
}
